/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useMemo } from 'react';
import { Platform, Text, View, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { surfaceCardStyles as styles } from './surfaceCards.styles';

export type EmbeddedMetricProgress = {
  ratio: number;
  atLimit?: boolean;
};

export type EmbeddedMetricProps = {
  label: string;
  value: string;
  warning?: boolean;
  /** Resalta el valor con color accent (ej. nombre del plan). */
  accent?: boolean;
  /** Color personalizado para el valor (ej. tier de plan). */
  valueColor?: string;
  /** Reduce el font-size para valores de texto largos (ej. rubro, sucursal). */
  compact?: boolean;
  /**
   * Montos largos (8+ cifras + decimales): una sola línea y achica la fuente
   * en vez de partir el valor en dos renglones.
   */
  fitValue?: boolean;
  progress?: EmbeddedMetricProgress | null;
  /** Parsea "used/max" del value para barra de progreso. */
  progressFromValue?: boolean;
  minWidth?: number;
  style?: StyleProp<ViewStyle>;
};

function parseUsageRatio(value: string): EmbeddedMetricProgress | null {
  if (!value.includes('/')) return null;
  const [usedStr, maxStr] = value.split('/');
  const used = Number(usedStr);
  const max = Number(maxStr);
  if (!Number.isFinite(used) || !Number.isFinite(max) || max <= 0) return null;
  return { ratio: Math.min(used / max, 1.5), atLimit: used >= max };
}

/** Métrica plana para embeber en cards/heroes/modales — sin borde propio. */
export function EmbeddedMetric({
  label,
  value,
  warning = false,
  accent = false,
  valueColor,
  compact = false,
  fitValue = false,
  progress,
  progressFromValue = false,
  minWidth = 120,
  style,
}: EmbeddedMetricProps) {
  const derivedProgress = useMemo(() => {
    if (progress !== undefined) return progress;
    if (progressFromValue) return parseUsageRatio(value);
    return null;
  }, [progress, progressFromValue, value]);

  const showProgress = derivedProgress != null;
  const atLimit = derivedProgress?.atLimit ?? false;
  const ratio = derivedProgress?.ratio ?? 0;

  return (
    <View
      style={[
        styles.embeddedMetric,
        { minWidth },
        warning && styles.embeddedMetricWarn,
        style,
      ]}
    >
      <Text style={styles.embeddedMetricLabel}>{label}</Text>
      <Text
        style={[
          styles.embeddedMetricValue,
          (compact || fitValue) && styles.embeddedMetricValueCompact,
          fitValue && styles.embeddedMetricValueFit,
          warning && styles.embeddedMetricValueWarn,
          accent && styles.embeddedMetricValueAccent,
          valueColor ? { color: valueColor } : null,
          // Prefer wrapping at word/hyphen boundaries; avoid Monotribut/o mid-word breaks.
          // Keep overflowWrap so long tokens can still use a 2nd line instead of "…".
          Platform.OS === 'web' && !fitValue
            ? ({ wordBreak: 'normal', overflowWrap: 'anywhere' } as unknown as TextStyle)
            : null,
        ]}
        numberOfLines={fitValue ? 1 : 2}
        adjustsFontSizeToFit={fitValue}
        minimumFontScale={fitValue ? 0.62 : 1}
      >
        {value}
      </Text>
      {showProgress ? (
        <View style={styles.embeddedMetricTrack}>
          <View
            style={[
              styles.embeddedMetricFill,
              { width: `${Math.min(ratio, 1) * 100}%` },
              warning && styles.embeddedMetricFillOver,
              !warning && atLimit && styles.embeddedMetricFillLimit,
            ]}
          />
        </View>
      ) : null}
    </View>
  );
}
